import React, { useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Radio, RadioGroup, Typography,
} from '@mui/material';
import { FileDownloadOutlined, FileUploadOutlined } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { integrationApiService, SpecCrosswalkPreview } from '../../../services/integrationApi';
import { useToast } from '../../../contexts/ToastContext';
import { tokens } from '../../../theme/tokens';
import type { SpecMetafieldRow } from '../../ShopifySpecMetafieldsSection';
import { applySpecCrosswalk, SpecCrosswalkMode } from '../specCrosswalkMerge';

interface SpecCrosswalkControlsProps {
  integrationId: string;
  rows: SpecMetafieldRow[];
  onChange: (rows: SpecMetafieldRow[]) => void;
  /** The plan lacks `specMappingCrosswalk`: the buttons explain instead of calling the server. */
  planLocked: boolean;
}

const ISSUES_SHOWN = 50;

/**
 * Spec mapping crosswalk (MOT-641, flag `spec_mapping_crosswalk`): download a CSV of the spec labels beside the
 * store's metafield definitions, fill it in, upload it. The upload is checked row by row and nothing is saved:
 * "Load into the table" puts the valid rows in the unsaved Spec → metafield table, and the page's Save writes them.
 */
const SpecCrosswalkControls: React.FC<SpecCrosswalkControlsProps> = ({ integrationId, rows, onChange, planLocked }) => {
  const { showToast } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState<(SpecCrosswalkPreview & { fileName: string }) | null>(null);
  const [mode, setMode] = useState<SpecCrosswalkMode>('merge');

  const download = async () => {
    setDownloading(true);
    try {
      const url = URL.createObjectURL(await integrationApiService.downloadSpecCrosswalk(integrationId));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'spec-mapping-crosswalk.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      showToast((error as Error).message, 'error');
    } finally {
      setDownloading(false);
    }
  };

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setChecking(true);
    try {
      setPreview({ ...(await integrationApiService.previewSpecCrosswalk(integrationId, file)), fileName: file.name });
      setMode('merge');
    } catch (error) {
      showToast((error as Error).message, 'error');
    } finally {
      setChecking(false);
    }
  };

  const result = preview ? applySpecCrosswalk(rows, preview.rows, mode, preview.maxMappings) : null;
  const fileProblem = preview?.errors.find((issue) => issue.row === 0);
  const rowErrors = preview?.errors.filter((issue) => issue.row > 0) || [];

  const load = () => {
    if (!result) return;
    onChange(result.rows);
    showToast(`Loaded ${preview?.rows.length ?? 0} mappings into the table. Save to keep them.`, 'success');
    setPreview(null);
  };

  const buttonSx = {
    height: 36, borderColor: tokens.line.default, borderRadius: `${tokens.radius.control}px`,
    fontWeight: 600, color: tokens.ink.body,
  } as const;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="outlined"
          disabled={planLocked || downloading}
          startIcon={downloading ? <CircularProgress size={14} /> : <FileDownloadOutlined />}
          onClick={() => { void download(); }}
          sx={buttonSx}
        >
          Download crosswalk
        </Button>
        <Button
          variant="outlined"
          disabled={planLocked || checking}
          startIcon={checking ? <CircularProgress size={14} /> : <FileUploadOutlined />}
          onClick={() => fileInput.current?.click()}
          sx={buttonSx}
        >
          Upload completed file
        </Button>
        <input ref={fileInput} type="file" accept=".csv,text/csv" hidden data-testid="spec-crosswalk-file" onChange={(e) => { void onFile(e); }} />
        {planLocked && (
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
            Spec crosswalk is included with Pro and above.
          </Typography>
        )}
      </Box>

      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Check crosswalk file</DialogTitle>
        <DialogContent dividers>
          {preview && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography sx={{ fontSize: 13, color: tokens.ink.muted }} noWrap>{preview.fileName}</Typography>
              {fileProblem ? (
                <Alert severity="error">{fileProblem.message}</Alert>
              ) : (
                <>
                  <Typography sx={{ fontSize: 14 }} data-testid="spec-crosswalk-summary">
                    {preview.rows.length} of {preview.totalRows - preview.blank - preview.unmapped} mapped rows are ready
                    {rowErrors.length > 0 ? `; ${rowErrors.length} need fixing` : ''}.
                    {preview.unmapped > 0 ? ` ${preview.unmapped} rows have no metafield and are left as they are.` : ''}
                  </Typography>
                  {rowErrors.length > 0 && (
                    <Alert severity="error" data-testid="spec-crosswalk-errors">
                      Rows with a problem are left out. Fix them in the file and upload it again, or load the rest.
                      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                        {rowErrors.slice(0, ISSUES_SHOWN).map((issue) => (
                          <li key={`${issue.row}-${issue.message}`}>Row {issue.row}: {issue.message}</li>
                        ))}
                      </Box>
                      {rowErrors.length > ISSUES_SHOWN && <Box sx={{ mt: 0.5 }}>and {rowErrors.length - ISSUES_SHOWN} more.</Box>}
                    </Alert>
                  )}
                  {preview.catalogTruncated && (
                    <Alert severity="warning">
                      This integration syncs more products than the crosswalk reads, so a spec label found only on later products is reported as not found.
                    </Alert>
                  )}
                  {preview.warnings.length > 0 && (
                    <Alert severity="warning">
                      {preview.warnings.length} rows may leave some products blank.
                      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                        {preview.warnings.slice(0, 5).map((issue) => (
                          <li key={`${issue.row}-${issue.message}`}>Row {issue.row}: {issue.message}</li>
                        ))}
                      </Box>
                    </Alert>
                  )}
                  {preview.rows.length > 0 && result && (
                    <>
                      <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as SpecCrosswalkMode)}>
                        <FormControlLabel value="merge" control={<Radio size="small" />} label="Add to my current mappings (default)" />
                        <FormControlLabel value="replace" control={<Radio size="small" />} label="Replace all my mappings with this file" />
                      </RadioGroup>
                      <Typography sx={{ fontSize: 13, color: tokens.ink.muted }} data-testid="spec-crosswalk-effect">
                        {result.added} new, {result.changed} changed, {result.unchanged} already the same
                        {result.removed > 0 ? `, ${result.removed} current mappings removed` : ''}.
                        Nothing is saved yet: the rows load into the table and the page&apos;s Save keeps them.
                      </Typography>
                      {result.overLimit && (
                        <Alert severity="warning">
                          That is more than the {preview.maxMappings} mappings a store can keep. Save would drop the last ones.
                        </Alert>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Cancel</Button>
          <Button variant="contained" disabled={!preview || preview.rows.length === 0 || Boolean(fileProblem)} onClick={load}>
            Load {preview?.rows.length ?? 0} mappings into the table
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * The controls for a view that does not already hold the integration's spec options (the flag-off Mapping layout):
 * reads the shared options query and renders nothing unless the crosswalk flag is on.
 */
export const SpecCrosswalkBar: React.FC<Omit<SpecCrosswalkControlsProps, 'planLocked'>> = (props) => {
  const optionsQuery = useQuery({
    queryKey: ['shopify-spec-metafield-options', props.integrationId],
    queryFn: () => integrationApiService.getSpecMetafieldOptions(props.integrationId),
    staleTime: 5 * 60 * 1000,
  });
  const crosswalk = optionsQuery.data?.specCrosswalk;
  return crosswalk ? <SpecCrosswalkControls {...props} planLocked={crosswalk.planLocked} /> : null;
};

export default SpecCrosswalkControls;
